/**
 * iCalendar (RFC 5545) generation.
 *
 * Deterministic by construction: UID and DTSTAMP come from the caller, never from
 * a clock, so the same draft always produces byte-identical output — which is what
 * makes the generator testable and safe to run on a server or in a browser.
 *
 * Zones are emitted as `TZID` parameters without a bundled `VTIMEZONE`; clients
 * resolve IANA names themselves, and we carry no tz database (see model.ts).
 */

import { LIMITS, sanitizeEmail, sanitizeLine, sanitizeText, sanitizeUrl } from './sanitize';
import {
  type EventDraft,
  type EventPerson,
  type EventRecurrence,
  type EventTime,
  effectiveEnd,
  timeClock,
  timeDate,
} from './model';

export interface IcsOptions {
  /** Stable identity for the event; reuse it when re-exporting an edited draft. */
  readonly uid?: string;
  /** Creation stamp in iCalendar UTC form, e.g. `20260815T090000Z` — see `icsTimestamp`. */
  readonly dtstamp: string;
  /** `PRODID` value; defaults to this package. */
  readonly productId?: string;
  /** `METHOD`; `REQUEST` turns the file into an invitation. */
  readonly method?: 'PUBLISH' | 'REQUEST';
}

const DEFAULT_PRODUCT_ID = '-//project//event-format//EN';
const CRLF = '\r\n';

/** Formats a `Date` as an iCalendar UTC timestamp (`20260815T090000Z`). */
export function icsTimestamp(when: Date): string {
  return `${when.toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`;
}

/**
 * Escapes a text value per RFC 5545 §3.3.11 — after stripping control characters, because
 * escaping alone wouldn't stop a raw CR from starting a new content line. The writer never
 * assumes its draft was sanitized; a hostile value must not be able to inject a property.
 */
function escapeText(value: string, maxLength: number = LIMITS.description): string {
  return sanitizeText(value, maxLength)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\n|\r/g, '\\n');
}

function utf8Length(codePoint: number): number {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
}

/** Folds a content line to 75 octets per RFC 5545 §3.1, never splitting a code point. */
export function foldLine(line: string): string {
  const parts: string[] = [];
  let current = '';
  let bytes = 0;
  let limit = 75;
  for (const char of line) {
    const size = utf8Length(char.codePointAt(0) ?? 0);
    if (bytes + size > limit) {
      parts.push(current);
      current = '';
      bytes = 1; // the leading space of a continuation line
      limit = 75;
    }
    current += char;
    bytes += size;
  }
  parts.push(current);
  return parts.join(`${CRLF} `);
}

function compactDate(date: string): string {
  return date.replace(/-/g, '');
}

function addDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

/** `DTSTART`/`DTEND` property with its value type or zone parameter. */
function timeProperty(
  name: 'DTSTART' | 'DTEND',
  time: EventTime,
  exclusiveEndDay: boolean,
): string {
  if (time.kind === 'date') {
    const date = exclusiveEndDay ? addDays(time.date, 1) : time.date;
    return `${name};VALUE=DATE:${compactDate(date)}`;
  }
  const stamp = `${compactDate(timeDate(time))}T${(timeClock(time) ?? '00:00:00').replace(/:/g, '')}`;
  if (time.timeZone === undefined) return `${name}:${stamp}`;
  if (time.timeZone === 'UTC') return `${name}:${stamp}Z`;
  const zone = paramValue(time.timeZone, 100);
  return zone === '' ? `${name}:${stamp}` : `${name};TZID=${zone}:${stamp}`;
}

/**
 * A parameter value ends at `:`, `;`, or a quote, so those can't survive inside one — an
 * unescapable case where dropping the character is the only safe move (RFC 5545 §3.2).
 */
function paramValue(value: string, maxLength: number): string {
  return sanitizeLine(value, maxLength).replace(/[";:,]/g, '');
}

/** Emits a person, or nothing at all when the address can't be trusted in a `mailto:`. */
function personProperty(name: 'ORGANIZER' | 'ATTENDEE', person: EventPerson): string | null {
  const email = sanitizeEmail(person.email);
  if (email === null) return null;
  const common =
    person.name === undefined ? '' : `;CN=${paramValue(person.name, LIMITS.personName)}`;
  const role = name === 'ATTENDEE' ? ';ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE' : '';
  return `${name}${common}${role}:mailto:${email}`;
}

export function recurrenceRule(recurrence: EventRecurrence): string {
  const parts = [`FREQ=${recurrence.frequency.toUpperCase()}`];
  if (recurrence.interval !== undefined && recurrence.interval !== 1)
    parts.push(`INTERVAL=${recurrence.interval}`);
  if (recurrence.count !== undefined) parts.push(`COUNT=${recurrence.count}`);
  if (recurrence.until !== undefined) parts.push(`UNTIL=${compactDate(recurrence.until)}T235959Z`);
  return `RRULE:${parts.join(';')}`;
}

function alarmLines(minutesBefore: number): string[] {
  return [
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder',
    `TRIGGER:-PT${minutesBefore}M`,
    'END:VALARM',
  ];
}

function veventLines(draft: EventDraft, options: IcsOptions): string[] {
  const rawUid = options.uid ?? draft.uid;
  if (rawUid === undefined) throw new Error('toIcs needs a uid, on the draft or in options.');
  // UID and DTSTAMP are caller-supplied, which makes them another untrusted input.
  const uid = paramValue(rawUid, 200);
  const dtstamp = /^\d{8}T\d{6}Z$/.test(options.dtstamp) ? options.dtstamp : '19700101T000000Z';

  const lines: string[] = ['BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${dtstamp}`];
  lines.push(timeProperty('DTSTART', draft.start, false));
  lines.push(timeProperty('DTEND', effectiveEnd(draft), draft.start.kind === 'date'));
  lines.push(`SUMMARY:${escapeText(draft.title, LIMITS.title)}`);
  if (draft.description !== undefined && draft.description !== '') {
    lines.push(`DESCRIPTION:${escapeText(draft.description)}`);
  }
  if (draft.location !== undefined && draft.location !== '')
    lines.push(`LOCATION:${escapeText(draft.location)}`);
  // A URL is emitted verbatim, so anything but a plain http(s) URL is dropped rather than escaped.
  const url = draft.url === undefined ? null : sanitizeUrl(draft.url);
  if (url !== null) lines.push(`URL:${url}`);
  if (draft.status !== undefined) lines.push(`STATUS:${draft.status.toUpperCase()}`);
  // OPAQUE is the format's default, but an explicit choice is worth writing down: it is the
  // difference between an event that blocks a calendar's free/busy and one that doesn't.
  if (draft.showAs !== undefined)
    lines.push(`TRANSP:${draft.showAs === 'free' ? 'TRANSPARENT' : 'OPAQUE'}`);
  if (draft.recurrence !== undefined) lines.push(recurrenceRule(draft.recurrence));
  const organizer =
    draft.organizer === undefined ? null : personProperty('ORGANIZER', draft.organizer);
  if (organizer !== null) lines.push(organizer);
  for (const attendee of (draft.attendees ?? []).slice(0, LIMITS.attendees)) {
    const property = personProperty('ATTENDEE', attendee);
    if (property !== null) lines.push(property);
  }
  for (const minutes of (draft.remindersMinutesBefore ?? []).slice(0, LIMITS.reminders)) {
    if (Number.isFinite(minutes) && minutes >= 0) lines.push(...alarmLines(Math.trunc(minutes)));
  }
  lines.push('END:VEVENT');
  return lines;
}

function calendarLines(body: string[], options: IcsOptions): string[] {
  const header = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${escapeText(options.productId ?? DEFAULT_PRODUCT_ID, 200)}`,
    'CALSCALE:GREGORIAN',
  ];
  if (options.method !== undefined) header.push(`METHOD:${options.method}`);
  return [...header, ...body, 'END:VCALENDAR'];
}

/** The full `.ics` text for one event, CRLF-terminated and folded. */
export function toIcs(draft: EventDraft, options: IcsOptions): string {
  return calendarLines(veventLines(draft, options), options).map(foldLine).join(CRLF).concat(CRLF);
}

/** One `.ics` holding several events — each draft supplies its own `uid`. */
export function toIcsCalendar(drafts: readonly EventDraft[], options: IcsOptions): string {
  const body = drafts
    .slice(0, LIMITS.events)
    .flatMap((draft) => veventLines(draft, { ...options, uid: draft.uid ?? options.uid }));
  return calendarLines(body, options).map(foldLine).join(CRLF).concat(CRLF);
}

/** A filename-safe `.ics` name derived from the title. */
export function icsFileName(draft: EventDraft): string {
  const slug = draft.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug === '' ? 'event' : slug}.ics`;
}
